// V.1.0.1 2025/08/20 11:00:10
import React, { useEffect, useState, Suspense, lazy } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDashboard, saveDashboard } from '../utils/db';
import { db, storage } from '../firebase';
import { doc, getDoc } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import ErrorBoundary from './ErrorBoundary';

// Helper function to compare semantic versions (e.g., "1.0.10" > "1.0.2")
const isVersionGreaterOrEqual = (v1, v2) => {
  if (!v1 || !v2) return false;
  const parts1 = v1.split('.').map(Number);
  const parts2 = v2.split('.').map(Number);
  const len = Math.max(parts1.length, parts2.length);
  for (let i = 0; i < len; i++) {
    const p1 = parts1[i] || 0;
    const p2 = parts2[i] || 0;
    if (p1 > p2) return true;
    if (p1 < p2) return false;
  }
  return true;
};

const DashboardWrapper = () => {
  const { dashboardIdParams } = useParams();
  const dashboardIdToLoad = dashboardIdParams;

  const { i18n, t } = useTranslation();

  const [dashboardModule, setDashboardModule] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardResourcesReady, setDashboardResourcesReady] = useState(false);

  useEffect(() => {
    const loadDashboard = async () => {
      setDashboardModule(null);
      setIsLoading(true);
      setError(null);
      setDashboardResourcesReady(false);

      const scriptId = `dynamic-dashboard-${dashboardIdToLoad}`;

      const loadAndInitializeDashboard = (module) => {
        if (module.getTranslations) {
          const translations = module.getTranslations();
          if (translations.en) i18n.addResourceBundle('en', 'translation', translations.en, true, true);
          if (translations.es) i18n.addResourceBundle('es', 'translation', translations.es, true, true);
        }
        if (module.initStyles) module.initStyles();
        setDashboardResourcesReady(true);
        setDashboardModule(module);
        setIsLoading(false);
      };

      if (window.RESEYE_DYNAMIC_DASHBOARDS && window.RESEYE_DYNAMIC_DASHBOARDS[dashboardIdToLoad]) {
        loadAndInitializeDashboard(window.RESEYE_DYNAMIC_DASHBOARDS[dashboardIdToLoad]);
        return;
      }

      try {
        const docRef = doc(db, "dashboards", dashboardIdToLoad);
        const docSnap = await getDoc(docRef);

        if (!docSnap.exists()) {
          throw new Error(`Dashboard metadata not found for ID: ${dashboardIdToLoad}`);
        }

        const { version: liveVersion, container } = docSnap.data();
        if (!liveVersion || !container) {
          throw new Error(`Incomplete metadata in Firestore for ${dashboardIdToLoad}. 'version' and 'container' fields are required.`);
        }

        const cachedDashboard = await getDashboard(dashboardIdToLoad);

        if (cachedDashboard && cachedDashboard.version && isVersionGreaterOrEqual(cachedDashboard.version, liveVersion)) {
          console.log(`[DashboardWrapper] Using cached v${cachedDashboard.version} for ${dashboardIdToLoad}. Live v${liveVersion}.`);
          const script = document.createElement('script');
          script.id = scriptId;
          script.textContent = cachedDashboard.code;
          document.body.appendChild(script);
          if (window.RESEYE_DYNAMIC_DASHBOARDS && window.RESEYE_DYNAMIC_DASHBOARDS[dashboardIdToLoad]) {
            loadAndInitializeDashboard(window.RESEYE_DYNAMIC_DASHBOARDS[dashboardIdToLoad]);
          } else {
            throw new Error(`Failed to initialize module from cache for ${dashboardIdToLoad}`);
          }
          return;
        }

        console.log(`[DashboardWrapper] Cache miss or old version (cached: ${cachedDashboard?.version}, live: ${liveVersion}). Fetching from Storage.`);

        const storagePath = `${container}/${dashboardIdToLoad}`;
        const storageRef = ref(storage, storagePath);
        const bundleUrl = await getDownloadURL(storageRef);

        const existingScript = document.getElementById(scriptId);
        if (existingScript) existingScript.remove();

        const script = document.createElement('script');
        script.id = scriptId;
        script.src = bundleUrl;
        script.async = true;
        document.body.appendChild(script);

        script.onload = async () => {
          if (window.RESEYE_DYNAMIC_DASHBOARDS && window.RESEYE_DYNAMIC_DASHBOARDS[dashboardIdToLoad]) {
            const module = window.RESEYE_DYNAMIC_DASHBOARDS[dashboardIdToLoad];
            loadAndInitializeDashboard(module);

            // Fetch code again to save it to cache
            const response = await fetch(bundleUrl);
            const bundleCode = await response.text();
            await saveDashboard(dashboardIdToLoad, {
              code: bundleCode,
              version: module.version,
              name: module.name,
            });
            console.log(`[DashboardWrapper] Dashboard '${module.name}' (v${module.version}) downloaded and cached.`);
          } else {
            throw new Error(`Module not found in window after script load for ${dashboardIdToLoad}`);
          }
        };

        script.onerror = () => {
          throw new Error(`Failed to load script from URL: ${bundleUrl}`);
        };

      } catch (err) {
        console.error("[DashboardWrapper] Fatal error loading dashboard:", err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    loadDashboard();

    return () => {
      const scriptId = `dynamic-dashboard-${dashboardIdToLoad}`;
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.onload = null;
        existingScript.onerror = null;
      }
    };
  }, [dashboardIdToLoad, i18n, t]);

  if (isLoading) {
    return <div style={{ padding: '20px' }}>Loading Dashboard...</div>;
  }

  if (error) {
    return <div style={{ padding: '20px', color: 'red' }}>Error: {error}</div>;
  }

  if (dashboardModule && dashboardModule.Component) {
    const DashboardComponent = dashboardModule.Component;
    if (dashboardModule.isLocal) {
      return (
        <ErrorBoundary>
          <Suspense fallback={<div style={{ padding: '20px' }}>Loading Dashboard...</div>}>
            <DashboardComponent />
          </Suspense>
        </ErrorBoundary>
      );
    } else {
      if (dashboardResourcesReady) {
        return (
          <ErrorBoundary>
            <DashboardComponent tFunction={t} />
          </ErrorBoundary>
        );
      } else {
        return <div style={{ padding: '20px' }}>Loading Dashboard Resources...</div>;
      }
    }
  }

  return <div style={{ padding: '20px' }}>Dashboard '{dashboardIdToLoad}' is not available.</div>;
};

export default DashboardWrapper;